/**
 * [PURPOSE]: Registry of character-data field paths reserved for future
 *   v1.x systems. v1 code must NOT write to these paths; doing so would
 *   collide with the future system's schema and break migration.
 * [WHY]: Documented in D1 spec — Reserved Field Registry section. Each
 *   entry corresponds to a deferred system (combat panel, treasure UI,
 *   familiar/cohort entities, party bastion + defenders, metamagic UI,
 *   reaction triggers, harness telemetry, encounter/DM screen, map/token
 *   state, recipes/formulas, combat stats, player handouts).
 * [INTEGRATION]: Consumed by:
 *   1. The check:anti-patterns grep gate (build-time fail on writes)
 *   2. loadCharacter's _unknownFields preservation (round-trip safe)
 *   3. The runtime gate (D3 / #103) — see assertNoReservedWrites() below
 * [SPEC]: docs/superpowers/specs/2026-04-28-d1-v1-migration-design.md
 *   — section "Reserved Field Registry"
 */

export const RESERVED_FIELDS = [
  // Combat panel (v1.1)
  'combatState',
  'combatRoundResources',

  // Treasure / loot generator (v1.1)
  'lootHistory',
  'encounterTreasureSeeds',

  // Familiar / cohort / sidekick entities (v1.1, part of D2)
  'companions',
  'activeFamiliarId',

  // Bastion defenders + party-linked bastion (v1.1, D2)
  'bastion.defenders',
  'bastion.permissions',
  'bastion.linkedPartyId',

  // Metamagic application UI (v1.1)
  'spellCastModifiers',

  // Combat-reaction triggers (v1.1)
  'reactionTriggers',

  // Generative E2E harness telemetry (post-publish)
  '_harnessTelemetry',

  // Encounter / DM screen data (v1.x extrapolated)
  'dmTools',

  // Map / token state (v1.x extrapolated)
  'mapState',

  // Recipes / formulas known (v1.x extrapolated)
  'recipesKnown',
  'formulaeBookId',

  // Combat history / damage statistics (v1.x extrapolated)
  'combatStats',

  // Player handouts (v1.x extrapolated)
  'handouts'
];

/**
 * [PURPOSE]: Map each reserved path to the v1.x system that owns it. Used
 *   when the runtime gate fires so the error message points at the right
 *   v1.1 plan / spec section.
 * [WHY]: Helpful diagnostics — when a future v1 commit accidentally writes
 *   to `combatState`, the dev sees "reserved for the v1.1 Combat Panel"
 *   instead of just "reserved path written".
 * [SPEC]: docs/superpowers/specs/2026-04-28-d2-deferred-system-migration-plans.md
 */
export const RESERVED_FIELD_OWNERS = {
  combatState: 'v1.1 Combat Panel / Turn Tracker',
  combatRoundResources: 'v1.1 Combat Panel / Turn Tracker',
  lootHistory: 'v1.1 Treasure / Loot Generator',
  encounterTreasureSeeds: 'v1.1 Treasure / Loot Generator',
  companions: 'v1.1 Familiar / Cohort / Sidekick entities (D2 system 3)',
  activeFamiliarId: 'v1.1 Familiar / Cohort / Sidekick entities (D2 system 3)',
  'bastion.defenders': 'v1.1 Bastion Defenders (D2 system 4)',
  'bastion.permissions': 'v1.1 Party-Linked Bastion permissions (D2 system 4)',
  'bastion.linkedPartyId': 'v1.1 Party-Linked Bastion (D2 system 4)',
  spellCastModifiers: 'v1.1 Metamagic Application UI',
  reactionTriggers: 'v1.1 Combat-Reaction Triggers',
  _harnessTelemetry: 'Generative E2E Harness telemetry (post-publish)',
  dmTools: 'v1.x Encounter / DM Screen',
  mapState: 'v1.x Map / Token State',
  recipesKnown: 'v1.x Recipes / Formulas',
  formulaeBookId: 'v1.x Recipes / Formulas',
  combatStats: 'v1.x Combat History / Damage Statistics',
  handouts: 'v1.x Player Handouts',
};

/**
 * Walks an object along a dot-delimited path and returns the value at that
 * path, or undefined if any segment is missing. No prototype-walking — the
 * walker stops if any intermediate is null/undefined or not an object.
 *
 * @param {Object|null|undefined} obj
 * @param {string} path Dot-delimited path (e.g., 'bastion.defenders').
 * @returns {*}
 */
function getAtPath(obj, path) {
  if (obj == null || typeof obj !== 'object') return undefined;
  const segments = path.split('.');
  let cur = obj;
  for (const seg of segments) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[seg];
  }
  return cur;
}

/**
 * Heuristic: is the value at a reserved path "meaningful content" worth
 *   gating on? An undefined/null value is a no-op (the path doesn't exist).
 *   Empty arrays and empty plain objects are also no-ops — historically,
 *   v1 code initialized `bastion.defenders: []` and that empty array is a
 *   no-op write tolerated by the v1.x migration (defenders ||= []). The
 *   gate matches that tolerance.
 *
 *   A populated array/object, a non-empty string, or any non-zero number/
 *   true/false flag IS a write we want to flag.
 *
 * @param {*} v
 * @returns {boolean}
 */
function isMeaningful(v) {
  if (v == null) return false; // undefined or null
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  if (typeof v === 'string') return v.length > 0;
  return true; // numbers / booleans — even 0 / false are meaningful primitives
}

/**
 * Find every reserved path in `nextState` that holds meaningful content
 *   AND differs from `prevState` at the same path (treating reference
 *   equality as "spread-through, not a write").
 *
 * The reference-equality short-circuit is the key to letting v1.x data
 *   round-trip through v1 without false positives. When a v1 update spreads
 *   prev — `setPlayerStats(prev => ({ ...prev, foo: 'bar' }))` — any
 *   reserved sub-object that lived under prev gets the same reference in
 *   next. We treat that as a non-write.
 *
 *   A genuine write — `setPlayerStats(prev => ({ ...prev, bastion: { ...prev.bastion, defenders: [...] }}))`
 *   creates a NEW bastion object. The reserved path `bastion.defenders` then
 *   has a different reference (or a new value), and the gate fires.
 *
 * @param {Object|null|undefined} nextState
 * @param {Object|null|undefined} prevState
 * @returns {Array<{ path: string, owner: string, value: * }>}
 */
export function findReservedWrites(nextState, prevState) {
  const violations = [];
  if (nextState == null || typeof nextState !== 'object') return violations;

  for (const path of RESERVED_FIELDS) {
    const nextValue = getAtPath(nextState, path);
    if (!isMeaningful(nextValue)) continue;

    const prevValue = getAtPath(prevState, path);
    // Reference-equal? It's spread-through, not a write.
    if (prevValue === nextValue) continue;

    violations.push({
      path,
      owner: RESERVED_FIELD_OWNERS[path] || 'a v1.x deferred system',
      value: nextValue,
    });
  }

  return violations;
}

/**
 * Runtime gate. Throws in development/test environments when v1 code
 *   attempts to write to a reserved path; logs and returns in production
 *   (consistent with systemBus's degrade-gracefully posture).
 *
 * @param {Object|null|undefined} nextState The state v1 is about to commit.
 * @param {Object|null|undefined} prevState The state before this update.
 * @param {string} [origin='setPlayerStats'] Caller name for the error message.
 * @throws {Error} in dev/test on any reserved write.
 */
export function assertNoReservedWrites(nextState, prevState, origin = 'setPlayerStats') {
  const violations = findReservedWrites(nextState, prevState);
  if (violations.length === 0) return;

  // Build a single helpful error message. Multiple violations get collapsed
  // into a list so tests fail with the full picture, not one path at a time.
  const lines = violations.map(({ path, owner }) =>
    `  - "${path}" — reserved for ${owner}`
  );
  const msg = (
    `[${origin}] Attempted to write reserved character-data path(s):\n` +
    `${lines.join('\n')}\n\n` +
    `These paths are forward-compat slots for v1.x deferred systems. v1 ` +
    `must NOT write to them — see src/services/reservedFields.js and the ` +
    `D1 migration spec for the full list. If a load/import path is ` +
    `legitimately round-tripping v1.x data, the spread-through check (` +
    `reference-equal prev/next values) should have suppressed this; ` +
    `examine the call site for an unintended new-object construction.`
  );

  // Test environment: jest sets NODE_ENV='test'. Throw so failing tests
  // surface the violation immediately.
  // Development: react-scripts sets NODE_ENV='development'. Same treatment.
  // Production: log and return (don't crash a player's session over a bug
  // we'd rather catch in CI / dev anyway).
  const env = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV) || 'production';
  if (env === 'production') {
    // eslint-disable-next-line no-console
    console.error(msg);
    return;
  }
  throw new Error(msg);
}
